import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TicketCard } from "@/components/TicketCard";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const { status } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [reservation, setReservation] = useState(null);
  const [tries, setTries] = useState(0);

  const rid = params.get("rid");
  const wid = params.get("w");

  useEffect(() => {
    if (status === "cancelado") { setState("cancelled"); return; }
    if (status === "agotado") { setState("soldout"); return; }
    if (status === "exito" && rid) {
      api.get(`/reservations/id/${rid}`)
        .then(({ data }) => { setState("paid"); setReservation(data); sessionStorage.removeItem(`vivo_checkout_${data.workshop_id}`); })
        .catch(() => setState("error"));
      return;
    }
    if (status !== "exito" || !sessionId) { setState(status === "cancelado" ? "cancelled" : "error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setState("paid"); setReservation(data.reservation); return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
        if (tries < 10) setTimeout(() => setTries((t) => t + 1), 1800);
        else setState("error");
      } catch { if (active) setState("error"); }
    };
    poll();
    return () => { active = false; };
  }, [status, sessionId, rid, tries]);

  if (state === "checking") {
    return (
      <div className="min-h-[70vh] grid place-items-center text-center px-4">
        <div data-testid="payment-checking">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Confirmando tu pago…</p>
        </div>
      </div>
    );
  }

  if (state === "cancelled" || state === "error" || state === "soldout") {
    const soldout = state === "soldout";
    return (
      <div className="min-h-[70vh] grid place-items-center text-center px-4">
        <div className="max-w-md" data-testid="payment-failed">
          <XCircle className="w-14 h-14 text-destructive mx-auto" />
          <h1 className="mt-4 text-2xl font-extrabold">
            {soldout ? "Se agotaron los cupos" : state === "cancelled" ? "El pago no se completó" : "No pudimos confirmar el pago"}
          </h1>
          <p className="mt-2 text-muted-foreground" data-testid="payment-failed-msg">
            {soldout
              ? "Mientras pagabas, otra persona tomó el último cupo. Anulamos tu pago automáticamente: no se te cobrará nada."
              : "No se realizó ningún cobro y tu cupo no fue reservado. Tus datos quedaron guardados para que lo intentes de nuevo."}
          </p>
          {wid && !soldout
            ? <Button className="mt-6" onClick={() => navigate(`/taller/${wid}?pagar=1`)} data-testid="payment-retry-btn">Volver al taller e intentar de nuevo</Button>
            : <Button className="mt-6" onClick={() => navigate(wid ? `/taller/${wid}` : "/")} data-testid="payment-back-btn">{wid ? "Volver al taller" : "Volver al catálogo"}</Button>}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-12 text-center">
      <CheckCircle2 className="w-16 h-16 text-[hsl(145,42%,38%)] mx-auto" data-testid="payment-success" />
      <h1 className="mt-4 text-3xl font-extrabold">¡Reserva confirmada!</h1>
      <p className="mt-2 text-muted-foreground">
        Te enviamos el comprobante de pago y tu ticket por email. Aquí está tu ticket digital.
      </p>
      {reservation && <div className="mt-8"><TicketCard reservation={reservation} /></div>}
      <div className="mt-8 flex gap-3 justify-center">
        <Link to="/mis-tickets"><Button variant="outline">Ver mis tickets</Button></Link>
        <Link to="/"><Button>Explorar más talleres</Button></Link>
      </div>
    </div>
  );
}
